import { showCustomToast } from 'common/components/CustomToast/utils';
import { DocumentNode, TypedDocumentNode, useMutation } from '@apollo/client';
import {
  Callout,
  CommandBarButton,
  DefaultButton,
  DirectionalHint,
  Icon,
  Persona,
  PersonaSize,
  PrimaryButton,
  Separator,
  Stack,
  Text,
  TextField,
} from '@fluentui/react';
import { useBoolean, useId } from '@fluentui/react-hooks';
import clsx from 'clsx';
import { CloseButton } from 'common/components/Buttons/CloseButton';
import { useCommonStyles } from 'common/styles';
import {
  getGlobalDateFormat,
  getPrettierdate,
  getTimeFormat,
} from 'common/utils/dateFormats';
import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useStyles } from './index.styles';

// Define minimal NodeData interface
interface NodeData {
  id: string | null;
  name: string | null;
  isApproved?: boolean | null;
  isRejected?: boolean | null;
  statusDate?: string | null;
  requestedDate?: string | null;
  topic?: string | null;
  subTopic?: string | null;
  digitalSignature?: string | null;
  parentId?: string | null;
  isTree?: boolean | null;
  isBranch?: boolean | null;
  messageUserId: string | null;
}

interface GenericNodeViewProps<TNode extends NodeData, TMutation, TVariables> {
  data: TNode;
  addNewMessageMutation?:
    | DocumentNode
    | TypedDocumentNode<TMutation, TVariables>;
  getEntityId: (data: TNode) => string;
}

export const GenericNodeView = <TNode extends NodeData, TMutation, TVariables>({
  data,
  addNewMessageMutation,
  getEntityId,
}: GenericNodeViewProps<TNode, TMutation, TVariables>) => {
  const buttonId = useId(`callout-label${data.id}`);
  const nodeId = useId(`nodeId-label${data.id}`);
  const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] =
    useBoolean(false);
  const [
    detailsCardVisible,
    { setTrue: openDetailsCard, setFalse: closeDetailsCard },
  ] = useBoolean(false);

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const getStatus = () => {
    let status = '';
    if (data.isApproved === true) status = 'Approved';
    else if (data.isRejected === true) status = 'Declined';
    else status = 'Pending';
    return status;
  };

  const status = getStatus();
  const getStatusIcon = () =>
    status === 'Approved'
      ? 'accept'
      : status === 'Declined'
      ? 'cancel'
      : 'Clock';

  const getBlockColor = () => {
    switch (status) {
      case 'Approved':
        return styles.backgroundApprovedBlock;
      case 'Declined':
        return styles.backgroundDeclineBlock;
      default:
        return data.isTree
          ? styles.whiteBackground
          : styles.backgroundPendingBlock;
    }
  };

  const getHeaderColor = () => {
    switch (status) {
      case 'Approved':
        return styles.headerApprove;
      case 'Declined':
        return data.isTree ? styles.headerApprove : styles.headerDeclined;
      default:
        return styles.headerPending;
    }
  };

  const getDescriptionColor = () =>
    status === 'Approved'
      ? styles.descriptionColorApprove
      : styles.descriptionColor;

  const getIconColor = () =>
    status === 'Pending' ? styles.iconColorPending : styles.iconColor;

  function isEmpty(str: string) {
    return !str || str.length === 0;
  }

  const getTreeHeadingStyle = () => {
    if (data.isTree) return clsx(styles.treeHeadingStyle, getBlockColor());
  };

  const detailCardOpening =
    isEmpty(data.requestedDate!) &&
    isEmpty(data.topic!) &&
    isEmpty(data.subTopic!);

  return (
    <div style={{ position: 'relative' }}>
      {data.parentId && (
        <Handle
          type="target"
          position={Position.Top}
          id="a"
          style={{ background: '#555' }}
        />
      )}
      <Stack
        onMouseEnter={() => {
          if (!detailCardOpening) openDetailsCard();
        }}
        onMouseLeave={() => closeDetailsCard()}
        verticalAlign="space-between"
        className={
          data.isTree
            ? styles.treeContainer
            : clsx(
                styles.nodeContainer,
                status === 'Approved'
                  ? styles.backgroundApprove
                  : styles.backgroundDefault
              )
        }
      >
        <Stack id={nodeId} tokens={{ childrenGap: 10 }}>
          <Stack className={getTreeHeadingStyle()}>
            {!data.parentId && (
              <>
                <Text
                  variant="medium"
                  className={clsx(commonStyles.semibold, getHeaderColor())}
                  style={{ textAlign: 'center' }}
                >
                  Requestor
                </Text>
                <Separator />
              </>
            )}
            <Text
              variant={'mediumPlus'}
              className={clsx(commonStyles.semibold, getHeaderColor())}
              style={{ textAlign: 'center' }}
            >
              {data.name}
            </Text>
          </Stack>

          {data.statusDate && !data.isTree && (
            <Stack>
              <Text
                className={getDescriptionColor()}
                style={{ textAlign: 'center' }}
              >
                {`${getPrettierdate(
                  getGlobalDateFormat(data.statusDate!)
                )}, ${getTimeFormat(data.statusDate!)}`}
              </Text>
            </Stack>
          )}
        </Stack>

        {!data.isTree && (
          <Stack verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <Text
              className={clsx(getDescriptionColor(), styles.textviewStyles)}
              variant="xSmall"
            >
              {data.digitalSignature}
            </Text>
            {!data.isTree && (
              <CommandBarButton
                className={clsx(
                  getStatus() === 'Approved'
                    ? styles.msgButtonAppproved
                    : styles.msgButtonDeclined,
                  styles.msgButton
                )}
                id={buttonId}
                onClick={toggleIsCalloutVisible}
                text="Message"
                iconProps={{ iconName: 'comment' }}
              />
            )}
            <Stack
              horizontal
              tokens={{ childrenGap: 10 }}
              horizontalAlign="center"
              verticalAlign="center"
              className={clsx(getBlockColor(), styles.statusBlock)}
            >
              <Icon iconName={getStatusIcon()} className={getIconColor()} />
              <Text className={getIconColor()}>{status}</Text>
            </Stack>
          </Stack>
        )}
      </Stack>

      {!data.isBranch && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="a"
          style={{ background: '#555' }}
        />
      )}

      {isCalloutVisible && addNewMessageMutation && (
        <MessageCallout
          onclick={toggleIsCalloutVisible}
          buttonId={buttonId}
          data={data}
          addNewMessageMutation={addNewMessageMutation}
          getEntityId={getEntityId}
        />
      )}

      {detailsCardVisible && (
        <Callout
          directionalHint={DirectionalHint.topAutoEdge}
          calloutWidth={400}
          target={`#${nodeId}`}
        >
          <Stack tokens={{ childrenGap: 20, padding: 20 }}>
            {!isEmpty(data.requestedDate!) && (
              <DataView
                title={'Requested date'}
                content={getGlobalDateFormat(data.requestedDate!)}
              />
            )}
            {!isEmpty(data.topic!) && (
              <DataView title={'Topic'} content={data.topic!} />
            )}
            {!isEmpty(data.subTopic!) && (
              <DataView title={'Subtopic'} content={data.subTopic!} />
            )}
          </Stack>
        </Callout>
      )}
    </div>
  );
};

interface MessageCalloutProps<TNode extends NodeData, TMutation, TVariables> {
  onclick: () => void;
  buttonId: string;
  data: TNode;
  addNewMessageMutation:
    | DocumentNode
    | TypedDocumentNode<TMutation, TVariables>;
  getEntityId: (data: TNode) => string;
}

const MessageCallout = <TNode extends NodeData, TMutation, TVariables>({
  onclick,
  buttonId,
  data,
  addNewMessageMutation,
  getEntityId,
}: MessageCalloutProps<TNode, TMutation, TVariables>) => {
  const [addNewMessage] = useMutation<TMutation, TVariables>(
    addNewMessageMutation,
    { errorPolicy: 'all' }
  );
  const [message, setmessage] = useState<string | undefined>();

  const onSendMessage = async () => {
    const { errors } = await addNewMessage({
      variables: {
        input: {
          entityId: getEntityId(data),
          message: {
            messageComment: message!,
            isResponseRequired: false,
            reminderDates: null,
          },
          department: null,
          communicationChannel: null,
          user: [data.messageUserId],
        },
      } as TVariables,
    });
    if (errors?.length)
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    else {
      onclick();
      showCustomToast({
        content: 'Message has been sent',
        appearance: 'success',
      });
    }
  };

  const sendBtnDisabled = message?.length === 0 || message === undefined;
  return (
    <Callout calloutWidth={500} target={`#${buttonId}`}>
      <Stack tokens={{ childrenGap: 20, padding: 20 }}>
        <Stack horizontal horizontalAlign="space-between">
          <Text block variant="xLarge">
            Send message
          </Text>
          <CloseButton onClick={onclick} />
        </Stack>
        <Persona text={data.name!} size={PersonaSize.size24} />
        <TextField
          multiline
          placeholder="Enter message"
          onChange={(_, value) => {
            setmessage(value);
          }}
        />
        <Stack horizontal horizontalAlign="space-between">
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton
              disabled={sendBtnDisabled}
              onClick={onSendMessage}
              text="Send message"
            />
            <DefaultButton onClick={onclick} text="Cancel" />
          </Stack>
        </Stack>
      </Stack>
    </Callout>
  );
};

interface DataViewProps {
  title: string;
  content: string;
}

const DataView: React.FC<DataViewProps> = ({ title, content }) => {
  const commonStyles = useCommonStyles();
  return (
    <Stack>
      <Text className={commonStyles.semibold} variant="medium">
        {title}
      </Text>
      <Text>{content}</Text>
    </Stack>
  );
};
